import { createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Pencil, Plus } from "lucide-react";
import { compareDecimals } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { formatUsdAmount } from "@/components/amount";
import { StatusBadge } from "@/components/status-badge";
import { cn, formatStatusLabel } from "@/lib/utils";
import { listAssets } from "@/api/assets";
import { deleteTariffRule, getTariff, upsertTariffRule } from "@/api/tariffs";
import { ApiError } from "@/api/client";
import { type TariffRuleItem, type UpsertTariffRuleInput } from "@/api/types";

// Real implementation of Task 29 Part D — the tariff rules editor, a brand
// new dynamic-segment route (same shape as Task 25's
// _app.merchants.$merchantId.tsx / Task 26's _app.invoices.$invoiceId.tsx).
// Reachable only via a row click from /tariffs, itself gated to
// CONFIG_MANAGER in _app.tsx's NAV_SECTIONS — no further per-control
// hasRole() gating needed on this page.
export const Route = createFileRoute("/_app/tariffs_/$tariffId")({
  component: TariffRulesPage,
});

const ERROR_BOX = "rounded-md border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2 text-sm text-[#EF4444]";
const SELECT_CLASSNAME = cn(
  "h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm shadow-sm",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
);

function BlockStatus({ label }: { label: string }) {
  return <div className="text-sm text-muted-foreground">{label}</div>;
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

// ─── add/edit rule dialog ────────────────────────────────────────────────────
//
// One shared form for both add and edit — PUT .../rules/:assetId is upsert
// semantics per the backend, so both flows call the same upsertTariffRule().
// fixedUsd/minUsd/maxUsd are plain text inputs (decimal strings, never cast
// to number) — percentBps is a real integer, not a monetary value, so a
// number input is fine there. The minUsd <= maxUsd pre-check uses
// @cpg/common's compareDecimals rather than Number(...) comparison, per this
// project's "no monetary string is ever summed/compared/derived on the
// frontend" rule; the backend's own 400 is still the source of truth and
// surfaces inline if this client-side check somehow disagrees with it.

interface RuleFormInitial {
  percentBps: number;
  fixedUsd: string;
  minUsd: string | null;
  maxUsd: string | null;
}

function UpsertRuleDialog({
  tariffId,
  assetId,
  assetLabel,
  initial,
  trigger,
}: {
  tariffId: string;
  assetId: string;
  assetLabel: string;
  initial?: RuleFormInitial;
  trigger: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [percentBps, setPercentBps] = useState(initial?.percentBps ?? 0);
  const [fixedUsd, setFixedUsd] = useState(initial?.fixedUsd ?? "0.00");
  const [minUsd, setMinUsd] = useState(initial?.minUsd ?? "");
  const [maxUsd, setMaxUsd] = useState(initial?.maxUsd ?? "");
  const [error, setError] = useState<string | null>(null);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setPercentBps(initial?.percentBps ?? 0);
      setFixedUsd(initial?.fixedUsd ?? "0.00");
      setMinUsd(initial?.minUsd ?? "");
      setMaxUsd(initial?.maxUsd ?? "");
      setError(null);
    }
  };

  const mutation = useMutation({
    mutationFn: () => {
      const input: UpsertTariffRuleInput = { percentBps, fixedUsd: fixedUsd.trim() };
      if (minUsd.trim()) input.minUsd = minUsd.trim();
      if (maxUsd.trim()) input.maxUsd = maxUsd.trim();
      return upsertTariffRule(tariffId, assetId, input);
    },
    onSuccess: () => {
      toast.success("Rule saved.");
      queryClient.invalidateQueries({ queryKey: ["tariff", tariffId] });
      setOpen(false);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Failed to save rule.");
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!Number.isInteger(percentBps) || percentBps < 0) {
      setError("Percent (bps) must be a non-negative integer.");
      return;
    }
    if (!fixedUsd.trim()) {
      setError("Fixed fee is required.");
      return;
    }
    // Optional client-side pre-check for a snappier UX — the backend
    // (TariffsService.upsertRule) still rejects with a 400 if this
    // disagrees, surfaced via the same inline error box on failure.
    if (minUsd.trim() && maxUsd.trim() && compareDecimals(minUsd.trim(), maxUsd.trim()) > 0) {
      setError("minUsd must not exceed maxUsd.");
      return;
    }
    mutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {initial ? "Edit rule" : "Add rule"} — {assetLabel}
          </DialogTitle>
          <DialogDescription>Fee rule applied to invoices settled in this asset.</DialogDescription>
        </DialogHeader>

        {error && <div className={ERROR_BOX}>{error}</div>}

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="rule-percentBps">Percent (bps)</Label>
            <Input
              id="rule-percentBps"
              type="number"
              min={0}
              step={1}
              value={percentBps}
              onChange={(e) => setPercentBps(Number(e.target.value))}
            />
            <p className="text-xs text-muted-foreground">{(percentBps / 100).toFixed(2)}%</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rule-fixedUsd">Fixed fee (USD)</Label>
            <Input id="rule-fixedUsd" value={fixedUsd} onChange={(e) => setFixedUsd(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="rule-minUsd">Min (USD, optional)</Label>
              <Input id="rule-minUsd" value={minUsd} onChange={(e) => setMinUsd(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rule-maxUsd">Max (USD, optional)</Label>
              <Input id="rule-maxUsd" value={maxUsd} onChange={(e) => setMaxUsd(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending} className="w-full sm:w-auto">
              {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save rule"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── rules table ─────────────────────────────────────────────────────────────

function RulesTable({ tariffId, rules }: { tariffId: string; rules: TariffRuleItem[] }) {
  const queryClient = useQueryClient();

  // No confirmation dialog — the plan doesn't call rule removal out as
  // consequential the way tariff-delete or merchant-access-revoke are (a
  // rule can simply be re-added via the same upsert form), matching the
  // merchant detail page's own no-confirm asset-disable toggle.
  const deleteMutation = useMutation({
    mutationFn: (assetId: string) => deleteTariffRule(tariffId, assetId),
    onSuccess: () => {
      toast.success("Rule removed.");
      queryClient.invalidateQueries({ queryKey: ["tariff", tariffId] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "Failed to remove rule.");
    },
  });

  if (rules.length === 0) return <BlockStatus label="No per-asset rules configured yet." />;

  return (
    <div className="overflow-hidden rounded-lg border">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted text-left text-xs font-semibold text-muted-foreground">
            <th className="px-4 py-2.5 font-semibold">Asset</th>
            <th className="px-4 py-2.5 font-semibold">Network</th>
            <th className="px-4 py-2.5 font-semibold">Percent</th>
            <th className="px-4 py-2.5 font-semibold">Fixed</th>
            <th className="px-4 py-2.5 font-semibold">Min</th>
            <th className="px-4 py-2.5 font-semibold">Max</th>
            <th className="px-4 py-2.5 font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rules.map((rule) => (
            <tr key={rule.assetId} className="border-t">
              <td className="px-4 py-2 font-medium">{rule.assetSymbol}</td>
              <td className="px-4 py-2">
                <StatusBadge label={formatStatusLabel(rule.assetNetwork)} color="neutral" />
              </td>
              <td className="px-4 py-2" style={{ fontVariantNumeric: "tabular-nums" }}>
                {rule.percentBps / 100}%
              </td>
              <td className="px-4 py-2" style={{ fontVariantNumeric: "tabular-nums" }}>
                {formatUsdAmount(rule.fixedUsd)}
              </td>
              <td className="px-4 py-2" style={{ fontVariantNumeric: "tabular-nums" }}>
                {rule.minUsd !== null ? formatUsdAmount(rule.minUsd) : "—"}
              </td>
              <td className="px-4 py-2" style={{ fontVariantNumeric: "tabular-nums" }}>
                {rule.maxUsd !== null ? formatUsdAmount(rule.maxUsd) : "—"}
              </td>
              <td className="px-4 py-2">
                <div className="flex items-center gap-2">
                  <UpsertRuleDialog
                    tariffId={tariffId}
                    assetId={rule.assetId}
                    assetLabel={`${rule.assetSymbol} · ${rule.assetNetwork}`}
                    initial={{
                      percentBps: rule.percentBps,
                      fixedUsd: rule.fixedUsd,
                      minUsd: rule.minUsd,
                      maxUsd: rule.maxUsd,
                    }}
                    trigger={
                      <Button type="button" variant="outline" size="sm">
                        <Pencil className="h-3.5 w-3.5" />
                        Edit
                      </Button>
                    }
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={deleteMutation.isPending}
                    onClick={() => deleteMutation.mutate(rule.assetId)}
                  >
                    Remove
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── add-rule control ────────────────────────────────────────────────────────

function AddRuleControl({ tariffId, rules }: { tariffId: string; rules: TariffRuleItem[] }) {
  const {
    data: assetsResponse,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["assets"],
    queryFn: listAssets,
  });
  const [selectedAssetId, setSelectedAssetId] = useState("");

  if (isPending) return <BlockStatus label="Loading assets…" />;
  if (isError) return <BlockStatus label="Failed to load assets." />;

  const unruledAssets = assetsResponse.data.filter(
    (asset) => !rules.some((rule) => rule.assetId === asset.id),
  );

  if (unruledAssets.length === 0) {
    return <BlockStatus label="Every asset already has a rule for this tariff." />;
  }

  const effectiveAssetId = unruledAssets.some((asset) => asset.id === selectedAssetId)
    ? selectedAssetId
    : unruledAssets[0].id;
  const effectiveAsset = unruledAssets.find((asset) => asset.id === effectiveAssetId)!;

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="space-y-1.5">
        <Label htmlFor="add-rule-asset">Asset</Label>
        <select
          id="add-rule-asset"
          className={SELECT_CLASSNAME}
          value={effectiveAssetId}
          onChange={(e) => setSelectedAssetId(e.target.value)}
        >
          {unruledAssets.map((asset) => (
            <option key={asset.id} value={asset.id}>
              {asset.symbol} · {asset.network}
            </option>
          ))}
        </select>
      </div>
      <UpsertRuleDialog
        key={effectiveAssetId}
        tariffId={tariffId}
        assetId={effectiveAssetId}
        assetLabel={`${effectiveAsset.symbol} · ${effectiveAsset.network}`}
        trigger={
          <Button type="button">
            <Plus className="h-4 w-4" />
            Add rule
          </Button>
        }
      />
    </div>
  );
}

// ─── page ────────────────────────────────────────────────────────────────────

function TariffRulesPage() {
  const { tariffId } = Route.useParams();

  const { data: tariff, isPending, isError } = useQuery({
    queryKey: ["tariff", tariffId],
    queryFn: () => getTariff(tariffId),
  });

  return (
    <div className="space-y-6">
      {isPending ? (
        <BlockStatus label="Loading tariff…" />
      ) : isError || !tariff ? (
        <BlockStatus label="Failed to load tariff." />
      ) : (
        <>
          <div>
            <h2 className="text-lg font-semibold">{tariff.name}</h2>
            <p className="text-sm text-muted-foreground">{tariff.publicId}</p>
          </div>

          <SectionCard title="Rules" description="Per-asset fee rules for this tariff.">
            <RulesTable tariffId={tariffId} rules={tariff.rules} />
          </SectionCard>

          <SectionCard title="Add rule" description="Assign a fee rule to an asset not yet covered.">
            <AddRuleControl tariffId={tariffId} rules={tariff.rules} />
          </SectionCard>
        </>
      )}
    </div>
  );
}
